import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';

export interface LuxuryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidthClass?: string;
  ariaLabel?: string;
}

export const LuxuryModal: React.FC<LuxuryModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  maxWidthClass = 'max-w-3xl',
  ariaLabel = 'Dialog Modal',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Apply complete body scroll lock and keyboard accessibility
  useModalScrollLock({ isOpen, onClose, containerRef });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 md:p-8 overflow-hidden select-none pointer-events-auto">
          {/* Soft Premium Backdrop Overlay with Warm Tone, Blur & Vignette */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={onClose}
            className="fixed inset-0 bg-[#FBF8F2]/80 backdrop-blur-md sm:backdrop-blur-lg shadow-inner pointer-events-auto"
            style={{
              backgroundImage: 'radial-gradient(ellipse at center, rgba(251, 248, 242, 0.7) 0%, rgba(60, 45, 34, 0.2) 100%)',
            }}
          />

          {/* Luxury Modal Card */}
          <motion.div
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : ariaLabel}
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={`relative z-10 w-full ${maxWidthClass} max-h-[90vh] flex flex-col bg-[#F7F2E8] border border-[#D8C29B] rounded-[24px] sm:rounded-[32px] text-[#3C2D22] shadow-[0_24px_60px_-15px_rgba(60,45,34,0.2),0_4px_16px_rgba(60,45,34,0.1)] overflow-hidden pointer-events-auto`}
          >
            {/* Modal Header */}
            <div className="shrink-0 px-6 sm:px-10 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-[#D8C29B]/40 flex items-start justify-between gap-4 relative bg-[#F7F2E8]/90 backdrop-blur-sm z-20">
              <div className="space-y-1 sm:space-y-1.5 pr-8">
                {subtitle && (
                  <div className="flex items-center space-x-2 text-[#B5895A] text-[10px] sm:text-xs tracking-[0.25em] font-sans uppercase font-semibold">
                    {icon && <span className="shrink-0">{icon}</span>}
                    <span>{subtitle}</span>
                  </div>
                )}
                {title && (
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-[#3C2D22] font-normal leading-tight">
                    {title}
                  </h2>
                )}
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                aria-label="Close dialog"
                className="p-2 sm:p-2.5 rounded-full text-[#6A5948] hover:text-[#3C2D22] bg-[#F3ECE2] hover:bg-[#EAE0D2] border border-[#D8C29B]/60 transition-all duration-200 cursor-pointer shrink-0 shadow-xs focus:outline-none focus:ring-2 focus:ring-[#B5895A]"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Scrollable Modal Body */}
            <div
              data-lenis-prevent
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 sm:px-10 py-6 sm:py-8 space-y-6 sm:space-y-8 luxury-scrollbar font-sans text-sm sm:text-base leading-relaxed text-[#3C2D22]"
            >
              {children}
            </div>

            {/* Optional Modal Footer */}
            {footer && (
              <div className="shrink-0 px-6 sm:px-10 py-4 sm:py-6 border-t border-[#D8C29B]/40 bg-[#F3ECE2]/60 z-20">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
